import {Input, MultiSelect, Select, Stack, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import FolderSelector from 'file-browser/FolderSelector';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {useTagSchemaSummariesWithFields} from 'schemas/useTagSchemaSummariesWithFields';
import FieldToColumnMapper, {MapperItem} from 'spreadsheet-import-wizard/shared/FieldToColumnMapper';
import {JSONValue} from 'util/Types';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';

const INPUT_DATA_NAME = 'Input dataset';

const ENTITY_ID_MAPPING_KEY = 'id';

interface UpdateEntitiesConfig {
  schemaId?: string | null;
  columnMapping?: Record<string, string> | null;
  folderId?: string | null;
  nameColumn?: string | null;
  aliasColumns?: Array<string> | string | null;
  entityRegistryIdColumn?: string | null;
}

function getAliasColumns(aliasColumns: UpdateEntitiesConfig['aliasColumns']): Array<string> {
  if (Array.isArray(aliasColumns)) {
    return aliasColumns.filter((column): column is string => typeof column === 'string' && column !== '');
  }
  return aliasColumns ? [aliasColumns] : [];
}

function getSchemaFields(fieldsJson: unknown): Array<SchemaField> {
  return Array.isArray(fieldsJson) ? (fieldsJson as Array<SchemaField>) : [];
}

export default function UpdateEntitiesEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const typedConfig = config as UpdateEntitiesConfig;

  const tagSchemas = useTagSchemaSummariesWithFields();

  const {dataFramesInfos} = useStepConfigEditorContext();
  const inputColumns = getDataFrameInfoOrEmptyInfo(dataFramesInfos, INPUT_DATA_NAME).dataFrameSpec.columns;

  const columnMapping = useMemo(() => typedConfig.columnMapping ?? {}, [typedConfig.columnMapping]);

  const selectedSchema = useMemo(
    () => tagSchemas?.find((schema) => schema.id === typedConfig.schemaId) ?? null,
    [tagSchemas, typedConfig.schemaId]
  );

  const schemaFields = useMemo(
    () => getSchemaFields(selectedSchema?.fieldsJson).filter((field) => !field.isComputed),
    [selectedSchema]
  );

  const fieldRows = useMemo(
    () => schemaFields.map((field) => ({id: field.systemName, displayName: field.name})),
    [schemaFields]
  );

  const columnOptions: Array<MapperItem> = useMemo(
    () =>
      inputColumns.map((column) => ({
        id: column.systemName,
        displayName: column.displayName,
      })),
    [inputColumns]
  );

  const columnSelectData = useMemo(
    () => columnOptions.map((column) => ({value: column.id, label: column.displayName})),
    [columnOptions]
  );

  const setConfigValue = (path: Array<string>, value: JSONValue): void => {
    onChangeConfig(config, path, value);
  };

  const handleSchemaChange = (schemaId: string | null): void => {
    const nextColumnMapping: Record<string, string> = {};
    const idColumn = columnMapping[ENTITY_ID_MAPPING_KEY];
    if (idColumn != null) {
      nextColumnMapping[ENTITY_ID_MAPPING_KEY] = idColumn;
    }
    onChangeConfig(config, [], {
      ...config,
      schemaId,
      columnMapping: nextColumnMapping,
    });
  };

  const handleEntityIdColumnChange = (columnSystemName: string | null): void => {
    const next: Record<string, string> = {...columnMapping};
    if (columnSystemName) {
      next[ENTITY_ID_MAPPING_KEY] = columnSystemName;
    } else {
      delete next[ENTITY_ID_MAPPING_KEY];
    }
    setConfigValue(['columnMapping'], next);
  };

  const handleFieldMappingsChange = (fieldMappings: Record<string, string>): void => {
    const next: Record<string, string> = {...fieldMappings};
    const idColumn = columnMapping[ENTITY_ID_MAPPING_KEY];
    if (idColumn != null) {
      next[ENTITY_ID_MAPPING_KEY] = idColumn;
    }
    setConfigValue(['columnMapping'], next);
  };

  return (
    <Stack spacing="md">
      <div>
        <Input.Label required={true}>Schema</Input.Label>
        <Select
          searchable={true}
          clearable={true}
          placeholder="Search for an entity schema..."
          value={typedConfig.schemaId ?? null}
          disabled={disabled || tagSchemas == null}
          nothingFound="No schemas found"
          data={(tagSchemas ?? []).map((schema) => ({value: schema.id, label: schema.name}))}
          onChange={handleSchemaChange}
        />
      </div>

      <div>
        <Input.Label required={true}>Entity ID column</Input.Label>
        <Select
          searchable={true}
          clearable={true}
          placeholder="Select the dataset column holding entity IDs..."
          value={columnMapping[ENTITY_ID_MAPPING_KEY] ?? null}
          disabled={disabled}
          nothingFound="No columns available"
          data={columnSelectData}
          onChange={handleEntityIdColumnChange}
        />
      </div>

      <div>
        <Input.Label>Folder</Input.Label>
        <FolderSelector
          value={typedConfig.folderId ?? null}
          allowNone={true}
          showRegistries={true}
          enableRedesign={true}
          level="append"
          error=""
          readOnly={Boolean(disabled)}
          requireSubfolderCreatableAuth={false}
          showInventory={true}
          showNotebook={false}
          unsetOnFetchFail={false}
          checkExistingItemForDataModsMovement={false}
          noFolderText="Leave folder unchanged"
          onChange={(e) => {
            setConfigValue(['folderId'], e.target.value ?? null);
          }}
        />
      </div>

      <div>
        <Input.Label>Name column</Input.Label>
        <Select
          searchable={true}
          clearable={true}
          placeholder="Select the dataset column for entity names..."
          value={typedConfig.nameColumn ?? null}
          disabled={disabled}
          nothingFound="No columns available"
          data={columnSelectData}
          onChange={(value) => {
            setConfigValue(['nameColumn'], value ?? null);
          }}
        />
      </div>

      <div>
        <Input.Label>Alias columns</Input.Label>
        <MultiSelect
          searchable={true}
          clearable={true}
          placeholder="Select dataset columns for aliases (optional)..."
          value={getAliasColumns(typedConfig.aliasColumns)}
          disabled={disabled}
          nothingFound="No columns available"
          data={columnSelectData}
          onChange={(value) => {
            setConfigValue(['aliasColumns'], value.length > 0 ? value : null);
          }}
        />
      </div>

      <div>
        <Input.Label>Entity registry ID column</Input.Label>
        <Select
          searchable={true}
          clearable={true}
          placeholder="Select the dataset column for entity registry IDs..."
          value={typedConfig.entityRegistryIdColumn ?? null}
          disabled={disabled}
          nothingFound="No columns available"
          data={columnSelectData}
          onChange={(value) => {
            setConfigValue(['entityRegistryIdColumn'], value ?? null);
          }}
        />
      </div>

      <div>
        <Input.Label>Field mappings</Input.Label>
        {selectedSchema == null ? (
          <Text c="dimmed" size="sm">
            Select a schema to map its fields to dataset columns.
          </Text>
        ) : fieldRows.length === 0 ? (
          <Text c="dimmed" size="sm">
            This schema has no fields to map.
          </Text>
        ) : (
          <FieldToColumnMapper
            rows={fieldRows}
            columns={columnOptions}
            mappings={columnMapping}
            onMappingChange={handleFieldMappingsChange}
            disabled={disabled}
          />
        )}
      </div>
    </Stack>
  );
}
